import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Camera, Image as IconeGalerie, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import {
  API,
  FOCUS,
  entetes,
  estAnnulation,
  estNonAutorise,
  messageErreur,
  redimensionnerImage,
  ilYA,
} from "./outils";
import { useMaintenant } from "./EcranRecherche";

// 11 champs lus par l'IA (dont la date de délivrance)
const CHAMPS = [
  ["nom", "Nom"],
  ["prenom", "Prénom"],
  ["passeport", "N° passeport", true],
  ["nationalite", "Nationalité"],
  ["date_naissance", "Naissance"],
  ["sexe", "Sexe"],
  ["date_delivrance", "Délivrance"],
  ["date_expiration", "Expiration"],
  ["lieu_naissance", "Lieu de naissance"],
  ["lieu_delivrance", "Lieu de délivrance"],
  ["nin", "NIN", true],
];

export default function EcranScanner({ code, onFermer, onLogout }) {
  const [etat, setEtat] = useState("idle"); // idle | analyse | resultat | erreur
  const [resultat, setResultat] = useState(null);
  const [messageErr, setMessageErr] = useState("");
  const [fileAttente, setFileAttente] = useState([]);
  const [actualisation, setActualisation] = useState(false);
  const inputRef = useRef(null);
  const galerieRef = useRef(null);
  const maintenant = useMaintenant(30000);
  const onLogoutRef = useRef(onLogout);
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const chargerFile = useCallback(
    async (signal) => {
      setActualisation(true);
      try {
        const { data } = await axios.get(`${API}/passport/scans`, { headers: entetes(code), signal, timeout: 30000 });
        setFileAttente(data.scans || []);
      } catch (e) {
        if (estNonAutorise(e)) onLogoutRef.current();
        /* sinon silencieux (comme avant) */
      } finally {
        if (!signal || !signal.aborted) setActualisation(false);
      }
    },
    [code]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    chargerFile(ctrl.signal);
    return () => ctrl.abort();
  }, [chargerFile]);

  const onFichier = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permet de reprendre la même photo
    if (!file) return;
    setEtat("analyse");
    setMessageErr("");
    try {
      const image_base64 = await redimensionnerImage(file);
      const { data } = await axios.post(`${API}/passport/scan`, { image_base64 }, { headers: entetes(code), timeout: 90000 });
      setResultat(data);
      setEtat("resultat");
      chargerFile();
    } catch (err) {
      if (estAnnulation(err)) return;
      if (estNonAutorise(err)) {
        onLogoutRef.current();
        return;
      }
      setMessageErr(
        messageErreur(err, "Lecture impossible. Reprenez la photo bien à plat, nette, avec la bande MRZ visible.")
      );
      setEtat("erreur");
    }
  };

  const analyse = etat === "analyse";
  const nbChamps = resultat ? CHAMPS.filter(([cle]) => (resultat[cle] || "").toString().trim()).length : 0;

  return (
    <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]" data-testid="ecran-scanner">
      <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] flex items-center gap-2">
        <div className="flex flex-col flex-1 min-w-0">
          <h1 className="m-0 text-white text-base font-bold">Scanner passeport</h1>
          <span className="text-[#94A3B8] text-xs">Les champs arrivent au bureau automatiquement</span>
        </div>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer le scanner"
          data-testid="btn-fermer-scanner"
          className={`w-11 h-11 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
        >
          <X aria-hidden="true" className="w-5 h-5" strokeWidth={2.2} />
        </button>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-3 p-3 [&>*]:shrink-0">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFichier}
          className="hidden"
          data-testid="input-photo-passeport"
        />
        <input
          ref={galerieRef}
          type="file"
          accept="image/*"
          onChange={onFichier}
          className="hidden"
          data-testid="input-galerie-passeport"
        />

        <div className="bg-[#0F172A] rounded-2xl h-[200px] flex items-center justify-center relative" aria-hidden={!analyse}>
          <div className="w-[300px] max-w-[85%] h-[150px] border-2 border-[#5EEAD4] rounded-xl flex flex-col justify-end p-2.5 box-border">
            <div className="h-[34px] border border-dashed border-[#5EEAD4] rounded-md flex items-center justify-center text-[#99F6E4] text-xs font-semibold text-center">
              Bande MRZ (2 lignes) bien visible
            </div>
          </div>
          {analyse ? (
            <div role="status" className="absolute inset-0 rounded-2xl bg-[#0F172A]/80 flex flex-col items-center justify-center gap-2 text-white">
              <Loader2 aria-hidden="true" className="w-8 h-8 animate-spin text-[#5EEAD4]" />
              <span className="text-sm font-semibold">Lecture en cours…</span>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={analyse}
            data-testid="btn-prendre-photo"
            className={`h-[52px] rounded-xl bg-[#0F766E] text-white text-[15px] font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${FOCUS}`}
          >
            <Camera aria-hidden="true" className="w-5 h-5" />
            Photo
          </button>
          <button
            type="button"
            onClick={() => galerieRef.current && galerieRef.current.click()}
            disabled={analyse}
            data-testid="btn-galerie"
            className={`h-[52px] rounded-xl border border-[#CBD5E1] bg-white text-[#334155] text-[15px] font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${FOCUS}`}
          >
            <IconeGalerie aria-hidden="true" className="w-5 h-5" />
            Galerie
          </button>
        </div>

        {etat === "erreur" ? (
          <p role="alert" className="m-0 text-[#B91C1C] text-sm bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl py-2 px-3" data-testid="scan-erreur">
            {messageErr}
          </p>
        ) : null}

        {resultat ? (
          <section className="bg-white border border-[#E2E8F0] rounded-[14px] p-3.5 flex flex-col gap-2.5" data-testid="scan-resultat">
            <div className="flex items-center justify-between gap-2">
              <h2 className="m-0 text-[15px] font-bold">Dernière lecture</h2>
              <span
                className="text-xs font-bold rounded-lg px-2 py-[3px]"
                style={nbChamps === CHAMPS.length ? { background: "#D1FAE5", color: "#065F46" } : { background: "#FEF3C7", color: "#92400E" }}
              >
                {nbChamps} / {CHAMPS.length} champs
              </span>
            </div>
            {etat === "resultat" ? (
              <p className="m-0 flex items-start gap-1.5 text-xs text-[#334155]">
                <CheckCircle2 aria-hidden="true" className="w-4 h-4 text-[#047857] flex-none" />
                Passeport lu et ajouté à la file. La fiche est prête à être importée dans le logiciel bureau (bouton « Scans
                récents »). La photo n'a pas été conservée.
              </p>
            ) : null}
            <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-2.5 text-sm">
              {CHAMPS.map(([cle, libelle, mono], i) => (
                <div key={cle} className={i === CHAMPS.length - 1 ? "col-span-2" : ""} data-testid={`champ-${cle}`}>
                  <dt className="text-xs text-[#475569]">{libelle}</dt>
                  <dd className={`m-0 font-semibold break-words ${mono ? "font-mono" : ""}`}>{resultat[cle] || "—"}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <section className="bg-white border border-[#E2E8F0] rounded-[14px] px-3.5 py-1">
          <div className="flex items-center justify-between min-h-[44px]">
            <h2 className="m-0 text-[15px] font-bold">
              File d'attente du bureau <span className="text-[#475569] font-semibold">({fileAttente.length})</span>
            </h2>
            <button
              type="button"
              onClick={() => chargerFile()}
              aria-label="Actualiser la file"
              data-testid="btn-rafraichir-file"
              className={`w-11 h-11 flex items-center justify-center text-[#334155] rounded-lg ${FOCUS}`}
            >
              <RefreshCw aria-hidden="true" className={`w-[18px] h-[18px] ${actualisation ? "animate-spin" : ""}`} />
            </button>
          </div>
          {fileAttente.length === 0 ? (
            <p className="m-0 py-2 text-sm text-[#475569] border-t border-[#F1F5F9]">Aucun scan en attente.</p>
          ) : (
            <ul className="list-none m-0 p-0">
              {fileAttente.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 min-h-[40px] border-t border-[#F1F5F9] text-sm"
                  data-testid="file-item"
                >
                  <span className="font-semibold truncate">
                    {s.nom} {s.prenom}
                  </span>
                  <span className="text-[#475569] whitespace-nowrap text-[13px]">{ilYA(s.cree_le, maintenant) || s.passeport}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="m-0 pb-2 text-[11px] text-[#475569]">
            Ces scans s'importent depuis le logiciel bureau, puis disparaissent automatiquement.
          </p>
        </section>
      </main>
    </div>
  );
}
